import { Button, Group, MultiSelect, RangeSlider, Stack, Text } from '@mantine/core';
import { useDebouncedCallback } from '@mantine/hooks';
import { FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ResultsValuesResponse } from '../../data-access/types/results';

import { useColFilters } from '@data-access';

import { useResultsColFilter } from '../../hooks/use-results-col-filter';

type ResultsFiltersMenuProps = {
  values: ResultsValuesResponse;
};

const FarmFilter: FC<{ values: ResultsValuesResponse['farm'] }> = ({
  values,
}) => {
  const { t } = useTranslation();
  const [value, setFilter] = useResultsColFilter<string[]>('farm');
  return (
    <MultiSelect
      comboboxProps={{ withinPortal: false }}
      data={values.map((v) => ({ value: v.id.toString(), label: v.name }))}
      label={t('common.societe')}
      onChange={(v) => setFilter(v)}
      searchable
      value={(value as string[] | null | undefined) || []}
    />
  );
};

const OrganizationFilter: FC<{
  values: ResultsValuesResponse['organizations'];
}> = ({ values }) => {
  const { t } = useTranslation();
  const [value, setFilter] = useResultsColFilter<string[]>('organizations');
  return (
    <MultiSelect
      comboboxProps={{ withinPortal: false }}
      data={values.map((v) => ({ value: v.id.toString(), label: v.name }))}
      label={t('common.properties.organization')}
      onChange={(v) => setFilter(v)}
      searchable
      value={(value as string[] | null | undefined) || []}
    />
  );
};

const CampaignFilter: FC<{ values: ResultsValuesResponse['campaign'] }> = ({
  values,
}) => {
  const { t } = useTranslation();
  const [value, setFilter] = useResultsColFilter<string[]>('campaign');
  return (
    <MultiSelect
      comboboxProps={{ withinPortal: false }}
      data={values.map((v) => ({
        value: v.id.toString(),
        label: `${v.name} (${v.year})`,
      }))}
      label={t('common.campaign')}
      onChange={(v) => setFilter(v)}
      searchable
      value={(value as string[] | null | undefined) || []}
    />
  );
};

const SpeciesFilter: FC<{ values: ResultsValuesResponse['species'] }> = ({
  values,
}) => {
  const { t } = useTranslation();
  const [value, setFilter] = useResultsColFilter<string[]>('species');
  return (
    <MultiSelect
      comboboxProps={{ withinPortal: false }}
      data={values.map((v) => ({
        value: v.id.toString(),
        label: v.name,
      }))}
      label={t('common.species')}
      onChange={(v) => setFilter(v)}
      searchable
      value={(value as string[] | null | undefined) || []}
    />
  );
};

const CarbonStockFilter: FC<{ range: ResultsValuesResponse['carbon_stock'] }> = ({
  range,
}) => {
  const { t } = useTranslation();
  const [filterValue, setFilter] = useResultsColFilter<[number, number]>(
    'csr_carbon_stock',
  );

  const min = useMemo(() => Math.floor(range.min), [range.min]);
  const max = useMemo(() => Math.ceil(range.max), [range.max]);

  const [value, setValue] = useState<[number, number]>(() => {
    const v = filterValue as [number, number] | undefined;
    return v && v.length === 2 ? v : [min, max];
  });

  const commit = useDebouncedCallback((next: [number, number]) => {
    setFilter(next);
  }, 250);

  useEffect(() => {
    const v = filterValue as [number, number] | undefined;
    if (!v || v.length !== 2) {
      setValue([min, max]);
    }
  }, [filterValue, min, max]);

  return (
    <div>
      <Text fw={500} size="sm">
        {t('common.carbon_stock')}
      </Text>
      <RangeSlider
        label={(v) => `${v.toFixed(0)} tC/ha`}
        max={max}
        min={min}
        onChange={(next) => {
          setValue(next as [number, number]);
          commit(next as [number, number]);
        }}
        value={value}
      />
      <Group justify="space-between" mt={4}>
        <Text c="dimmed" size="xs">
          {min.toFixed(0)} tC/ha
        </Text>
        <Text c="dimmed" size="xs">
          {((min + max) / 2).toFixed(0)} tC/ha
        </Text>
        <Text c="dimmed" size="xs">
          {max.toFixed(0)} tC/ha
        </Text>
      </Group>
    </div>
  );
};

export const ResultsFiltersMenu: FC<ResultsFiltersMenuProps> = ({ values }) => {
  const { t } = useTranslation();
  const [, , resetFilters] = useColFilters('results');

  return (
    <Stack>
      <FarmFilter values={values.farm} />
      {values.organizations?.length ? (
        <OrganizationFilter values={values.organizations} />
      ) : null}
      <SpeciesFilter values={values.species} />
      <CampaignFilter values={values.campaign} />
      <CarbonStockFilter range={values.carbon_stock} />

      <Group justify="start">
        <Button color="red" onClick={() => resetFilters()} variant="light">
          {t('table.clearFilters')}
        </Button>
      </Group>
    </Stack>
  );
};

